import { useMemo } from "react";

import { ButtonsWrapper } from "components/elements";
import { useTiVoDeviceData } from "components/form/fields/TiVoTargets";
import { RELEASE_STAGES, isStatusReleaseStage } from "hooks/tivoApps";

import {
  PublicationsExportButton,
  PublicationsTable,
  PublicationRenderer,
} from "./components";
import { PublicationStatus } from "./components/PublicationStatus";
import { useTargets } from "./TargetsProvider";
import styles from "./TiVoPublicationsList.scss";
import { PublicationsTableProps } from "./types";
import { getReleaseStagePublication } from "./utils";

export function buildRenderPublicationSummary(
  deviceData: ReturnType<typeof useTiVoDeviceData>
): PublicationRenderer {
  return ({
    publication,
    platformId,
    oemId,
    applicableReleaseStages,
    clientTypeId,
  }) => {
    const releaseStagePublication = getReleaseStagePublication(
      publication,
      platformId,
      oemId,
      clientTypeId
    );
    const variantReleaseStages =
      publication.variant?.status.filter(isStatusReleaseStage) || [];

    return RELEASE_STAGES.map((releaseStage) => {
      const targets = (publication.variant?.versions || []).find((version) =>
        version.releaseStages?.includes(releaseStage)
      )?.targets;
      const isTargetValid = clientTypeId
        ? deviceData
            .getClientTypeIds([platformId], [oemId], targets)
            .includes(clientTypeId)
        : deviceData.getOemIds([platformId], targets).includes(oemId);

      return (
        <PublicationStatus
          key={releaseStage}
          publication={releaseStagePublication}
          releaseStage={releaseStage}
          variantReleaseStages={variantReleaseStages}
          applicableReleaseStages={applicableReleaseStages}
          isTargetValid={isTargetValid}
        />
      );
    });
  };
}

export function TiVoPublicationsSummaryTable({
  platforms,
  publications,
  status,
}: PublicationsTableProps) {
  const deviceData = useTiVoDeviceData();
  const { targets, setTargets } = useTargets();

  const renderPublicationSummary = useMemo(
    () => buildRenderPublicationSummary(deviceData),
    [deviceData]
  );

  return (
    <>
      <PublicationsTable
        colClassName={styles.summaryCol}
        platforms={platforms}
        publications={publications}
        status={status}
        targets={targets}
        setTargets={setTargets}
      >
        {renderPublicationSummary}
      </PublicationsTable>
      <ButtonsWrapper>
        <PublicationsExportButton platforms={platforms} targets={targets} />
      </ButtonsWrapper>
    </>
  );
}
